import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Upload, Send, Loader2, ExternalLink, Award, Shield, AlertTriangle, CheckCircle2, Camera, MapPin } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageCompression';
import { storage } from '../../services/firebaseService';
import { submitTesterPromotion } from '../../services/testerPromotionService';
import BetaCountriesPanel from './BetaCountriesPanel';
import { AppUser } from '../../types';

interface TesterStatusTabProps {
  user: AppUser;
}

const SHARE_TEXTS: Record<string, { label: string; text: string }[]> = {
  no: [
    {
      label: 'Facebook-gruppe',
      text: 'Jeg tester den nye Fricamp-appen som hjelper deg å finne de beste fricamp-stedene i Norge — uten å stå i kø! Prøv den gratis: https://fricamp.com'
    },
    {
      label: 'Personlig innlegg',
      text: 'Vet du at det finnes tusenvis av lovlige fristeder i norsk natur for fri camping med bobil og campingvogn? Fricamp viser deg alle sammen — og de er gratis! Sjekk den ut: https://fricamp.com'
    },
    {
      label: 'Instagram / Story',
      text: 'Endelig en app som viser deg hvor du faktisk KAN stå med bobil eller campingvogn i naturen 🏕️ Fricamp er i beta — prøv den! fricamp.com'
    },
    {
      label: 'Kort og enkel',
      text: 'Fricamp-appen finner de skjulte camping-perlene i Norge for deg 🇳🇴 Gratis å prøve: https://fricamp.com'
    }
  ],
  en: [
    {
      label: 'Facebook group',
      text: "I'm testing the new Fricamp app that helps you find the best free camping spots in Norway — no queues! Try it free: https://fricamp.com"
    },
    {
      label: 'Personal post',
      text: 'Did you know there are thousands of legal free camping spots in Norwegian nature? Fricamp shows you all of them — and they are free! Check it out: https://fricamp.com'
    },
    {
      label: 'Instagram / Story',
      text: "Finally an app that shows you where you CAN actually park your motorhome or caravan in nature 🏕️ Fricamp is in beta — try it! fricamp.com"
    },
    {
      label: 'Short & simple',
      text: 'Fricamp finds the hidden camping gems in Norway for you 🇳🇴 Free to try: https://fricamp.com'
    }
  ]
};

const TesterStatusTab: React.FC<TesterStatusTabProps> = ({ user }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'no';
  const texts = SHARE_TEXTS[lang] || SHARE_TEXTS.no;

  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [promoUrl, setPromoUrl] = useState('');
  const [promoDescription, setPromoDescription] = useState('');
  const [promoImage, setPromoImage] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const feedbackCount = user.feedback_count || 0;
  const promoCount = user.social_promo_count || 0;
  const isAmbassador = !!user.tester_valid_until;
  const hasMetRequirements = feedbackCount > 0 || promoCount >= 5;

  // Calculate days remaining
  let daysRemaining: number | null = null;
  if (user.tester_joined_at && !isAmbassador && !hasMetRequirements) {
    const joinedMs = user.tester_joined_at?.toMillis
      ? user.tester_joined_at.toMillis()
      : (user.tester_joined_at?.seconds ? user.tester_joined_at.seconds * 1000 : null);
    if (joinedMs) {
      daysRemaining = Math.max(0, 30 - Math.floor((Date.now() - joinedMs) / (24 * 60 * 60 * 1000)));
    }
  }

  const handleCopy = async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch { /* fallback not needed for modern browsers */ }
  };

  const handleSubmit = async () => {
    if (!promoUrl && !promoImage) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      let imageUrl: string | undefined;
      if (promoImage) {
        const { file: compressedPromo } = await compressImage(promoImage);
        const storageRef = ref(storage, `tester_promotions/${user.uid}/${Date.now()}_${compressedPromo.name}`);
        await uploadBytes(storageRef, compressedPromo);
        imageUrl = await getDownloadURL(storageRef);
      }
      await submitTesterPromotion({
        url: promoUrl || undefined,
        imageUrl,
        description: promoDescription || undefined
      });
      setSubmitResult({ ok: true, msg: t('profile.ambassador.submit_success', 'Sendt inn! Vi sjekker det snart.') });
      setPromoUrl('');
      setPromoDescription('');
      setPromoImage(null);
    } catch (e: any) {
      setSubmitResult({ ok: false, msg: e.message || t('profile.ambassador.submit_error', 'Noe gikk galt') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300 pb-8">
      {/* Status overview */}
      <div className={`p-4 rounded-2xl border ${
        isAmbassador
          ? 'bg-amber-500/10 border-amber-500/25'
          : hasMetRequirements
            ? 'bg-emerald-500/10 border-emerald-500/25'
            : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)]'
      }`}>
        <div className="flex items-center gap-3 mb-3">
          {isAmbassador ? (
            <Award size={20} className="text-amber-400" />
          ) : hasMetRequirements ? (
            <Shield size={20} className="text-emerald-400" />
          ) : (
            <AlertTriangle size={20} className="text-[var(--text-secondary)]" />
          )}
          <div>
            <p className="text-sm font-black text-[var(--text-primary)]">
              {isAmbassador
                ? t('profile.ambassador.status_ambassador', 'Ambassadør-status aktiv!')
                : hasMetRequirements
                  ? t('profile.ambassador.status_safe', 'Tilgangen din er sikret!')
                  : t('profile.ambassador.status_active', 'Testperiode aktiv')}
            </p>
            {daysRemaining !== null && !hasMetRequirements && (
              <p className={`text-2xs font-bold ${daysRemaining <= 5 ? 'text-red-400' : 'text-[var(--text-muted)]'}`}>
                {t('profile.ambassador.days_left', '{{days}} dager igjen', { days: daysRemaining })}
              </p>
            )}
            {isAmbassador && user.tester_valid_until && (
              <p className="text-2xs text-amber-400/80 font-bold">
                {t('profile.ambassador.valid_until', 'Gyldig til {{date}}', {
                  date: new Date(user.tester_valid_until?.seconds ? user.tester_valid_until.seconds * 1000 : user.tester_valid_until).toLocaleDateString()
                })}
              </p>
            )}
          </div>
        </div>

        {/* Progress bars */}
        <div className="space-y-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                {t('profile.ambassador.feedback_label', 'Tilbakemeldinger')}
              </span>
              <span className={`text-2xs font-bold ${feedbackCount > 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                {feedbackCount > 0 ? <CheckCircle2 size={10} className="inline mr-1" /> : null}
                {feedbackCount}
              </span>
            </div>
            <div className="h-1.5 bg-black/30 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${feedbackCount > 0 ? 'bg-emerald-500' : 'bg-stone-700'}`}
                   style={{ width: feedbackCount > 0 ? '100%' : '0%' }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                {t('profile.ambassador.shares_label', 'Delinger i sosiale medier')}
              </span>
              <span className={`text-2xs font-bold ${promoCount >= 5 ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
                {promoCount >= 5 ? <Award size={10} className="inline mr-1" /> : null}
                {promoCount} / 5
              </span>
            </div>
            <div className="h-1.5 bg-black/30 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${promoCount >= 5 ? 'bg-amber-500' : promoCount > 0 ? 'bg-amber-500/60' : 'bg-stone-700'}`}
                   style={{ width: `${Math.min(100, (promoCount / 5) * 100)}%` }} />
            </div>
          </div>
        </div>
      </div>


      {/* Land i test — Sverige/Danmark/Finland er PÅ som standard for testere */}
      <BetaCountriesPanel user={user} />

      {/* Section A: Pre-written texts */}
      <div>
        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
          <Copy size={11} className="inline mr-1.5 -mt-0.5" />
          {t('profile.ambassador.copy_texts_title', 'Ferdigskrevne tekster — kopier med ett klikk')}
        </p>
        <div className="space-y-2">
          {texts.map((item, idx) => (
            <div key={idx} className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{item.label}</span>
                <button
                  onClick={() => handleCopy(item.text, idx)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest transition-all ${
                    copiedIdx === idx
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20'
                  }`}
                >
                  {copiedIdx === idx ? <Check size={10} /> : <Copy size={10} />}
                  {copiedIdx === idx
                    ? t('profile.ambassador.copied', 'Kopiert!')
                    : t('profile.ambassador.copy', 'Kopier')}
                </button>
              </div>
              <p className="text-tiny text-[var(--text-secondary)] leading-relaxed italic">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Section B: Submit proof */}
      <div>
        <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
          <Send size={11} className="inline mr-1.5 -mt-0.5" />
          {t('profile.ambassador.submit_title', 'Registrer din deling')}
        </p>
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl p-4 space-y-3">
          <p className="text-2xs text-[var(--text-muted)] leading-relaxed">
            {t('profile.ambassador.submit_description', 'Last opp et skjermbilde av innlegget ditt — det er nok alene. Lenke er valgfritt, f.eks. hvis innlegget er offentlig.')}
          </p>

          <div>
            <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest block mb-1">
              <Camera size={9} className="inline mr-1 -mt-0.5" />
              {t('profile.ambassador.screenshot_label', 'Skjermbilde av innlegget')}
            </label>
            <label className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl cursor-pointer transition-colors ${promoImage ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[var(--bg-input)] border-[var(--border-secondary)] hover:border-white/20'}`}>
              <Camera size={14} className={promoImage ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
              <span className="text-xs text-[var(--text-secondary)] truncate flex-1">
                {promoImage ? promoImage.name : t('profile.ambassador.choose_file', 'Velg bilde...')}
              </span>
              {promoImage && (
                <button type="button" onClick={e => { e.preventDefault(); setPromoImage(null); }}
                  className="shrink-0 text-[var(--text-muted)] hover:text-red-400 transition-colors">
                  <span className="text-2xs">✕</span>
                </button>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => setPromoImage(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          <div>
            <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest block mb-1">
              {t('profile.ambassador.url_label', 'Lenke til innlegg')}
              <span className="ml-1 font-normal normal-case text-[var(--text-muted)]">({t('common.optional', 'valgfritt')})</span>
            </label>
            <input
              type="text"
              value={promoUrl}
              onChange={e => setPromoUrl(e.target.value)}
              placeholder="https://facebook.com/..."
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-violet-500/50"
            />
          </div>

          <div>
            <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest block mb-1">
              {t('profile.ambassador.desc_label', 'Kommentar (valgfritt)')}
            </label>
            <input
              type="text"
              value={promoDescription}
              onChange={e => setPromoDescription(e.target.value)}
              placeholder={t('profile.ambassador.desc_placeholder', 'F.eks. "Delt i Bobilgruppen på Facebook"')}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-violet-500/50"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || (!promoUrl && !promoImage)}
            className="w-full flex items-center justify-center gap-2 py-3 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 text-amber-300 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-40 transition-all"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {t('profile.ambassador.submit_btn', 'Send inn')}
          </button>

          {submitResult && (
            <p className={`text-2xs text-center ${submitResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
              {submitResult.msg}
            </p>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="bg-amber-500/5 border border-amber-500/15 rounded-2xl p-4">
        <p className="text-2xs text-amber-300/80 leading-relaxed">
          {t('profile.ambassador.info_text', 'Når du har 5 godkjente delinger, får du automatisk Ambassadør-status med gratis tilgang i ett helt år. Du kan også beholde tilgangen ved å sende tilbakemeldinger via Innboks & Feedback.')}
        </p>
      </div>
    </div>
  );
};

export default TesterStatusTab;
